import styled, { css, keyframes } from "styled-components";
import { fontFamilies } from "components/shared/tokens/typography";

const COLORS = {
  page: "#f5f7fc",
  white: "#ffffff",
  title: "#293142",
  muted: "#7d8599",
  line: "#e1e6ef",
  brand: "#ef4f5f",
  status: "#1479ff",
  statusBackground: "#e4eeff",
  unscheduledStatus: "#c45c00",
  unscheduledStatusBackground: "#fff1e6",
};

const MOBILE = "@media (max-width: 480px)";
const PAGE_MAX_WIDTH = "760px";

export const PageShell = styled.main`
  min-height: 100dvh;
  background: ${COLORS.page};
  color: ${COLORS.title};
  font-family: ${fontFamilies.regular};
`;

export const PageFrame = styled.div`
  width: min(100%, ${PAGE_MAX_WIDTH});
  min-height: 100vh;
  margin: 0 auto;
  background: ${COLORS.page};
  box-shadow: 0 0 30px rgba(41, 49, 66, 0.04);
`;

export const PageHeader = styled.header`
  position: sticky;
  top: 0;
  z-index: 10;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-height: 56px;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  box-sizing: border-box;
  background: ${COLORS.white};

  ${MOBILE} {
    min-height: 52px;
    padding: 6px 14px;
  }
`;

export const HeaderTitle = styled.h1`
  overflow: hidden;
  margin: 0;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 14px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${MOBILE} {
    font-size: 14px;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 12px;

  ${MOBILE} {
    gap: 8px;
  }
`;

export const HeaderIconButton = styled.button`
  display: inline-flex;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  border: 1px solid ${COLORS.line};
  border-radius: 50%;
  padding: 0;
  background: ${COLORS.white};
  color: ${COLORS.title};
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
    stroke-width: 2;
  }

  &:disabled {
    cursor: default;
  }

  &:not(:disabled):active {
    transform: scale(0.97);
  }

  ${MOBILE} {
    width: 36px;
    height: 36px;
    flex-basis: 36px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

export const SearchBar = styled.div`
  display: flex;
  min-height: 48px;
  align-items: center;
  gap: 10px;
  border-top: 1px solid #f2f3f6;
  padding: 10px 24px;
  box-sizing: border-box;
  background: ${COLORS.white};
  color: ${COLORS.muted};

  svg {
    width: 20px;
    height: 20px;
  }

  ${MOBILE} {
    padding: 8px 16px;
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: ${COLORS.title};
  font-family: ${fontFamilies.regular};
  font-size: 15px;

  &::placeholder {
    color: #9ba2b3;
  }
`;

export const PrimaryTabs = styled.nav`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  min-height: 48px;
  background: ${COLORS.white};
`;

export const PrimaryTab = styled.button`
  position: relative;
  border: 0;
  padding: 0 16px;
  background: ${COLORS.white};
  color: ${(props) => (props.$active ? COLORS.title : "#9aa1b2")};
  font-family: ${fontFamilies.semiBold};
  font-size: 13px;
  line-height: 1.25;
  cursor: ${(props) => (props.$active ? "default" : "pointer")};

  &::after {
    content: "";
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 4px;
    background: ${(props) => (props.$active ? COLORS.brand : "transparent")};
  }

  &:disabled {
    cursor: default;
  }

  ${MOBILE} {
    font-size: 13px;
  }
`;

export const PageContent = styled.section`
  padding: 24px 24px 64px;

  ${MOBILE} {
    padding: 18px 16px 48px;
  }
`;

export const ScheduleTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
`;

export const ScheduleTab = styled.button`
  min-height: 36px;
  border: 1.5px solid ${(props) => (props.$active ? COLORS.brand : COLORS.line)};
  border-radius: 9px;
  padding: 0 14px;
  background: ${(props) => (props.$active ? "#fff4f5" : COLORS.white)};
  box-shadow: ${(props) =>
    props.$active ? "none" : "0 2px 6px rgba(41, 49, 66, 0.10)"};
  color: ${(props) => (props.$active ? COLORS.title : COLORS.muted)};
  font-family: ${fontFamilies.semiBold};
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:active {
    transform: scale(0.98);
  }

  ${MOBILE} {
    min-height: 34px;
    padding: 0 12px;
    font-size: 13px;
  }
`;

export const ScheduleCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ScheduleCard = styled.article`
  overflow: hidden;
  border-radius: 20px;
  padding: 0 18px 18px;
  background: ${(props) => (props.$disabled ? "#E8EAF5" : COLORS.white)};
  box-shadow: 0 3px 12px rgba(41, 49, 66, 0.07);
  cursor: ${(props) => (props.$disabled ? "default" : "pointer")};

  ${MOBILE} {
    border-radius: 16px;
    padding: 0 14px 14px;
  }
`;

export const ScheduleStatus = styled.span`
  display: inline-flex;
  min-height: 30px;
  align-items: center;
  border-radius: 0 0 4px 4px;
  padding: 0 10px;
  background: ${(props) =>
    props.$tone === "unscheduled"
      ? COLORS.unscheduledStatusBackground
      : COLORS.statusBackground};
  color: ${(props) =>
    props.$tone === "unscheduled" ? COLORS.unscheduledStatus : COLORS.status};
  font-family: ${fontFamilies.semiBold};
  font-size: 13px;

  ${MOBILE} {
    min-height: 27px;
    padding: 0 8px;
    font-size: 12px;
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
`;

export const CardTitle = styled.h2`
  margin: 0;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 15px;
  line-height: 1.25;

  ${MOBILE} {
    font-size: 14px;
  }
`;

export const CardSubtitle = styled.div`
  margin-top: 4px;
  color: ${COLORS.muted};
  font-family: ${fontFamilies.regular};
  font-size: 13px;
  line-height: 1.3;

  ${MOBILE} {
    font-size: 12px;
  }
`;

export const ScheduleArrow = styled.button`
  display: inline-flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  border: 1px solid ${COLORS.line};
  border-radius: 50%;
  padding: 0;
  background: ${COLORS.white};
  color: ${COLORS.brand};
  cursor: pointer;

  &:active {
    transform: scale(0.97);
  }

  ${MOBILE} {
    width: 36px;
    height: 36px;
    flex-basis: 36px;
  }
`;

export const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  margin-top: 18px;
  border: 1px solid ${COLORS.line};
  border-radius: 12px;
  padding: 12px 8px;
  background: ${(props) => (props.$disabled ? "#F4F6FB" : "transparent")};

  ${MOBILE} {
    margin-top: 15px;
    border-radius: 11px;
    padding: 10px 4px;
  }
`;

export const MetadataItem = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  text-align: center;
`;

export const MetadataIcon = styled.span`
  display: inline-flex;
  color: ${COLORS.muted};

  svg {
    width: 18px;
    height: 18px;
    stroke-width: 1.9;
  }

  ${MOBILE} {
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

export const MetadataValue = styled.span`
  max-width: 100%;
  overflow: hidden;
  color: ${COLORS.title};
  font-family: ${fontFamilies.regular};
  font-size: 13px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${MOBILE} {
    font-size: 12px;
  }
`;

export const EmptyState = styled.div`
  display: flex;
  min-height: 220px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 9px;
  border-radius: 18px;
  padding: 28px;
  background: ${COLORS.white};
  color: ${COLORS.muted};
  font-size: 14px;
  line-height: 1.45;
  text-align: center;
  box-shadow: 0 3px 12px rgba(41, 49, 66, 0.05);
`;

export const StateTitle = styled.h2`
  margin: 0;
  color: ${COLORS.title};
  font-family: ${fontFamilies.semiBold};
  font-size: 18px;
`;

export const AllCaughtUpState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 64px 28px;
  color: ${COLORS.muted};
  font-size: 14px;
  line-height: 1.45;
  text-align: center;
`;

export const AllCaughtUpIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 104px;
    height: 104px;
  }

  ${MOBILE} {
    img {
      width: 88px;
      height: 88px;
    }
  }
`;

export const AllCaughtUpTitle = styled.h2`
  margin: 0;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 20px;

  ${MOBILE} {
    font-size: 18px;
  }
`;

export const ErrorMessage = styled.span`
  color: #8b5260;
`;

export const RetryButton = styled.button`
  min-height: 40px;
  margin-top: 8px;
  border: 0;
  border-radius: 9px;
  padding: 0 18px;
  background: ${COLORS.brand};
  color: ${COLORS.white};
  font-family: ${fontFamilies.semiBold};
  font-size: 14px;
  cursor: pointer;
`;

const skeletonPulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
`;

const skeletonBackground = css`
  background: linear-gradient(90deg, #edf0f5 0%, #f7f8fa 50%, #edf0f5 100%);
  background-size: 200% 100%;
  animation: ${skeletonPulse} 1.2s ease-in-out infinite;
`;

export const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-radius: 22px;
  padding: 20px 24px 24px;
  background: ${COLORS.white};
`;

export const SkeletonLine = styled.div`
  width: ${(props) => props.$width || "100%"};
  height: ${(props) => props.$height || "16px"};
  border-radius: 8px;
  ${skeletonBackground}
`;

export const HelpSheetBackdrop = styled.div`
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
  background: rgba(20, 24, 32, 0.72);
`;

export const HelpSheetModal = styled.section`
  display: flex;
  width: 100%;
  max-width: ${PAGE_MAX_WIDTH};
  max-height: 75dvh;
  flex-direction: column;
  overflow: hidden;
  border-radius: 22px 22px 0 0;
  background: ${COLORS.page};
  box-shadow: 0 18px 52px rgba(17, 23, 36, 0.32);

  ${MOBILE} {
    max-width: 100%;
    border-radius: 18px 18px 0 0;
  }
`;

export const HelpSheetHeader = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px;

  ${MOBILE} {
    padding: 16px 18px;
  }
`;

export const HelpSheetTitle = styled.h2`
  margin: 0;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 20px;

  ${MOBILE} {
    font-size: 18px;
  }
`;

export const HelpSheetCloseButton = styled.button`
  display: grid;
  width: 36px;
  height: 36px;
  flex: none;
  place-content: center;
  border: 0;
  border-radius: 50%;
  background: ${COLORS.white};
  color: ${COLORS.title};
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const HelpSheetBody = styled.div`
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 24px 28px;

  img {
    display: block;
    width: 100%;
    height: auto;
    max-width: 100%;
    border-radius: 12px;
  }

  ${MOBILE} {
    padding: 0 18px 24px;
  }
`;
